import { useState, useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import useSnackbar from 'src/hooks/useCustomSnackbar';
import Iconify from '../Iconify';
import { createDentallyAppointmentAsync } from '../../utils/dentallyApi';
import { callApi } from '../../utils/apiCall';
import { hasFeatureAccess } from '../../utils/featurePermission';
import { resolveDentallyBookingAppointment } from '../../utils/dentallyAppointmentResolver';
import {
  buildPendingBookingFingerprint,
  completeDentallyPendingBooking,
  fetchDentallyPendingBooking,
} from '../../utils/dentallyPendingIntent';
import { nextStepCard, nextStepIcon, nextStepTitle, nextStepDescription, nextStepCTA } from '../../styles/OverView';

const SNACKBAR_ANCHOR = { vertical: 'top', horizontal: 'center' };

/**
 * Amber banner shown on the lead Overview tab when a deferred Dentally booking
 * is awaiting payment confirmation. Lets staff complete the Dentally booking
 * once payment has been confirmed out-of-band.
 *
 * Reads the allowlisted pending projection from the active reminder/task API.
 * Raw reminder metadata and lead metadata are never consumed here.
 */
export default function DentallyPendingBookingBanner({ practiceId, leadDetail = null, onComplete = null }) {
  const { enqueueSnackbar } = useSnackbar();
  const [completing, setCompleting] = useState(false);
  const [retryError, setRetryError] = useState(null);
  const [pendingData, setPendingData] = useState(null);
  const [pendingSource, setPendingSource] = useState(null);
  const localAppointmentRef = useRef({
    bookingKey: null,
    appointmentId: null,
    providerCompleted: false,
  });
  const loadSequenceRef = useRef(0);
  const leadId = leadDetail?.id;
  const operationGenerationRef = useRef(0);
  const activeContextRef = useRef({ practiceId, leadId });
  activeContextRef.current = { practiceId, leadId };

  useEffect(() => {
    loadSequenceRef.current += 1;
    operationGenerationRef.current += 1;
    const sequence = loadSequenceRef.current;
    setPendingData(null);
    setPendingSource(null);
    setRetryError(null);
    setCompleting(false);
    localAppointmentRef.current = {
      bookingKey: null,
      appointmentId: null,
      providerCompleted: false,
    };
    if (!practiceId || !leadId) return undefined;

    let active = true;
    const useTasksV2 = hasFeatureAccess('tasks-v2', practiceId);
    fetchDentallyPendingBooking(practiceId, leadId, { useTasksV2 }).then((resolution) => {
      if (!active || sequence !== loadSequenceRef.current) return;
      setPendingData(resolution.outcome === 'unique' ? resolution.pending : null);
      setPendingSource(resolution.outcome === 'unique' ? resolution.source : null);
    });
    return () => { active = false; };
  }, [practiceId, leadId, leadDetail?.updated_at]);

  const apptTimeLabel = (() => {
    if (!pendingData?.dateTime) return null;
    try {
      const appointmentDate = new Date(pendingData.dateTime);
      if (Number.isNaN(appointmentDate.getTime())) return null;
      return appointmentDate.toLocaleString('en-GB', {
        weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit', hour12: false,
      });
    } catch { return null; }
  })();

  const handleCompleteBooking = useCallback(async () => {
    if (!practiceId || !leadId || !pendingData || !pendingSource) return;
    const operationGeneration = operationGenerationRef.current + 1;
    operationGenerationRef.current = operationGeneration;
    const operationPracticeId = practiceId;
    const operationLeadId = leadId;
    const operationPending = pendingData;
    const operationSource = pendingSource;
    const isCurrentOperation = () => {
      const activeContext = activeContextRef.current;
      return operationGeneration === operationGenerationRef.current
        && String(activeContext.practiceId) === String(operationPracticeId)
        && String(activeContext.leadId) === String(operationLeadId);
    };
    setCompleting(true);
    setRetryError(null);
    try {
      const { practitionerId, dateTime, reason, method, assignees, notes, treatmentRecordId } = operationPending;
      const startDate = new Date(dateTime);
      if (Number.isNaN(startDate.getTime())) {
        setRetryError('The saved appointment date is invalid. Dentally was not contacted.');
        return;
      }
      const fingerprint = buildPendingBookingFingerprint(operationPracticeId, operationLeadId, operationPending);
      const startIso = fingerprint.startTime;
      const finishIso = fingerprint.finishTime;

      const bookingKey = JSON.stringify({
        practiceId: operationPracticeId,
        leadId: operationLeadId,
        startIso,
        finishIso,
        treatmentRecordId,
      });
      if (localAppointmentRef.current.bookingKey !== bookingKey) {
        localAppointmentRef.current = {
          bookingKey,
          appointmentId: null,
          providerCompleted: false,
        };
      }
      let {
        appointmentId: crmAppointmentId,
        providerCompleted,
      } = localAppointmentRef.current;

      // Reuse an exact persisted appointment when one exists. If none exists,
      // create once and retain the returned id for retries during this mount;
      // after a remount the nested appointment response is the source of truth.
      if (!crmAppointmentId) {
        const leadRes = await callApi(`/api/v1/practices/${operationPracticeId}/leads/${operationLeadId}/`);
        if (!isCurrentOperation()) return;
        if (!leadRes?.ok) {
          setRetryError('Could not check for an existing CRM appointment. Dentally was not contacted.');
          return;
        }
        const resolution = resolveDentallyBookingAppointment({
          lead: leadRes.data,
          fingerprint,
        });
        if (resolution.outcome === 'ambiguous') {
          setRetryError('Multiple matching CRM appointments were found. Dentally was not contacted.');
          return;
        }
        const { appointment } = resolution;
        const {
          id: persistedAppointmentId,
          dentally_id: dentallyId,
          dentally_appointment_id: dentallyAppointmentId,
        } = appointment || {};
        crmAppointmentId = persistedAppointmentId;
        providerCompleted = Boolean(dentallyId || dentallyAppointmentId);

        if (!crmAppointmentId) {
          const aRes = await callApi(`/api/v1/practices/${operationPracticeId}/leads/${operationLeadId}/appointments/`, 'post', {
            datetime: startIso,
            method: method || 'IN_PRACTICE',
            type: 'CONSULTATION',
            contacted_via: 'PHONE',
            lead_treatment: treatmentRecordId,
            assignees: assignees || [],
          });
          if (!isCurrentOperation()) return;
          if (!aRes.ok) {
            const msg = aRes.data?.detail || aRes.data?.message || 'Failed to create appointment';
            setRetryError(msg);
            return;
          }

          crmAppointmentId = aRes.data?.id;
          if (!crmAppointmentId) {
            setRetryError('CRM appointment was saved but no appointment ID was returned. Dentally was not contacted.');
            return;
          }
        }
        if (!isCurrentOperation()) return;
        localAppointmentRef.current = { bookingKey, appointmentId: crmAppointmentId, providerCompleted };
      }

      if (!providerCompleted) {
        const dRes = await createDentallyAppointmentAsync(operationPracticeId, {
          lead_id: operationLeadId,
          crm_appointment_id: crmAppointmentId,
          start_time: startIso,
          finish_time: finishIso,
          practitioner_id: practitionerId,
          reason: reason || 'Exam',
          ...(notes ? { notes } : {}),
        });
        if (!isCurrentOperation()) return;

        if (!dRes?.ok) {
          const detail = dRes?.data?.detail || dRes?.data?.message || 'Dentally sync failed';
          enqueueSnackbar(`Appointment saved in CRM. Note: ${detail}`, {
            variant: 'warning',
            anchorOrigin: SNACKBAR_ANCHOR,
          });
          setRetryError(`${detail}. The CRM appointment is saved and this action can be retried safely.`);
          return;
        }
        providerCompleted = true;
        localAppointmentRef.current = { bookingKey, appointmentId: crmAppointmentId, providerCompleted: true };
      }

      const sourceResult = await completeDentallyPendingBooking(operationPracticeId, operationSource);
      if (!isCurrentOperation()) return;
      if (!sourceResult.ok) {
        setRetryError(
          sourceResult.message
          || 'The Dentally booking succeeded, but the source task could not be completed. Retry to finish cleanup; Dentally will not be contacted again.'
        );
        return;
      }

      enqueueSnackbar('Dentally booking completed successfully', { variant: 'success', anchorOrigin: SNACKBAR_ANCHOR });
      setPendingData(null);
      setPendingSource(null);
      onComplete?.();
    } catch (err) {
      if (isCurrentOperation()) setRetryError(err?.message || 'An unexpected error occurred');
    } finally {
      if (isCurrentOperation()) setCompleting(false);
    }
  }, [practiceId, leadId, pendingData, pendingSource, enqueueSnackbar, onComplete]);

  if (!pendingData) return null;

  const AMBER = '#FFAB00';

  return (
    <Box sx={{ ...nextStepCard('rgba(255,171,0,0.08)'), mt: 2 }}>
      <Box sx={nextStepIcon(AMBER)}>
        <Iconify icon="mdi:cash-clock" width={24} height={24} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={nextStepTitle}>Dentally booking not yet completed</Typography>
        <Typography sx={nextStepDescription}>
          {apptTimeLabel ? `Slot: ${apptTimeLabel}` : 'Appointment slot saved'}
          {pendingData.treatmentName ? ` · ${pendingData.treatmentName}` : ''}
        </Typography>
        <Typography sx={{ ...nextStepDescription, mt: 0.25 }}>
          This slot has not been booked in Dentally yet. If a payment was requested, confirm it has been
          received, then book the appointment.
        </Typography>
        {retryError && (
          <Typography variant="caption" color="error" component="div" sx={{ mt: 0.5 }}>
            {retryError}
          </Typography>
        )}
      </Box>
      <LoadingButton
        variant="contained"
        loading={completing}
        onClick={handleCompleteBooking}
        startIcon={<Iconify icon="mdi:calendar-check" width={16} />}
        sx={{ ...nextStepCTA, bgcolor: AMBER, '&:hover': { bgcolor: AMBER, opacity: 0.9 }, flexShrink: 0 }}
      >
        Complete Dentally Booking
      </LoadingButton>
    </Box>
  );
}

DentallyPendingBookingBanner.propTypes = {
  practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  leadDetail: PropTypes.object,
  onComplete: PropTypes.func,
};
